import React, { useState } from "react";
import { LogIn, LogOut, Video, Send, CalendarDays, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink } from "../care/ui";
import { ParentSupport } from "./Complaints";
import { ParentLiveSection } from "./LiveParent";

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// Parent connect: check-in/out anytime, schedule native video meetings, message staff.
// mode="staff" renders the family-messages center for admins/teachers.
export default function ParentConnect({ mode = "parent", myChildren = [], checkins = [], meetings = [], messages = [], currentUser, ws }) {
  const [meetingForm, setMeetingForm] = useState({ title: "", date: "", time: "", childId: "", notes: "" });
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const isParent = mode === "parent";

  const latestFor = (childId) => checkins.find((k) => k.childId === childId);

  const toggleCheckin = async (childId) => {
    const latest = latestFor(childId);
    const direction = latest?.direction === "in" ? "out" : "in";
    try {
      await ws.checkChildInOut(childId, direction);
      toast.success(direction === "in" ? "Checked in — have a great day!" : "Checked out — see you soon!");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const schedule = async (e) => {
    e.preventDefault();
    try {
      await ws.scheduleMeeting({ ...meetingForm, childId: meetingForm.childId || null });
      toast.success("Meeting scheduled — native video room link created.");
      setShowMeetingForm(false);
      setMeetingForm({ title: "", date: "", time: "", childId: "", notes: "" });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgText.trim()) return;
    try {
      await ws.sendStaffMessage(msgText.trim(), isParent ? "staff" : replyTo);
      setMsgText("");
      toast.success("Message sent.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const familyThreads = {};
  if (!isParent) {
    messages.forEach((m) => {
      const key = m.fromRole === "parent" ? m.fromEmail : m.toEmail;
      if (key && key !== "staff") (familyThreads[key] = familyThreads[key] || []).push(m);
    });
  }

  return (
    <div className={`${ccCard} p-6 animate-fade-up`} data-testid={isParent ? "parent-connect" : "family-messages"}>
      <h3 className="text-base font-extrabold font-heading text-[#374151] flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-[#E5A0BC]" />
        </span>
        {isParent ? "Check-In, Meetings & Messages" : "Family Messages"}
      </h3>

      {isParent && (
        <div className="space-y-5">
          {/* Check-in / out */}
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Daily Check-In</p>
            {myChildren.length === 0 && <p className="text-xs text-[#9CA3AF] font-medium">No child linked yet.</p>}
            {myChildren.map((c) => {
              const latest = latestFor(c.id);
              const checkedIn = latest?.direction === "in";
              return (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl mb-2" data-testid={`checkin-row-${c.id}`}>
                  <div>
                    <p className="text-xs font-extrabold text-[#374151]">{c.firstName} {c.lastName}</p>
                    <p className="text-[11px] text-[#9CA3AF]" data-testid={`checkin-status-${c.id}`}>
                      {latest ? `${checkedIn ? "Checked in" : "Checked out"} ${fmtTime(latest.ts)} by ${latest.byName || latest.by}` : "Not checked in today"}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleCheckin(c.id)}
                    data-testid={`checkin-btn-${c.id}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${
                      checkedIn ? "bg-rose-50 border border-rose-200 text-rose-500 hover:bg-rose-100" : "bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] hover:bg-[#D2EBD1]"
                    }`}
                  >
                    {checkedIn ? <LogOut className="w-3.5 h-3.5" /> : <LogIn className="w-3.5 h-3.5" />}
                    {checkedIn ? "Check Out" : "Check In"}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Video meetings */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">On-Site Video Meetings</p>
              <button onClick={() => setShowMeetingForm((v) => !v)} data-testid="meeting-schedule-btn" className="text-[11px] font-bold text-[#5CB3DC] hover:underline">
                {showMeetingForm ? "Cancel" : "+ Schedule"}
              </button>
            </div>
            {showMeetingForm && (
              <form onSubmit={schedule} className="mb-3 p-4 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl grid grid-cols-2 gap-3" data-testid="meeting-schedule-form">
                <div className="col-span-2">
                  <label className={ccLabel}>Topic</label>
                  <input required value={meetingForm.title} onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })} className={ccInput} placeholder="e.g. Progress chat" data-testid="meeting-title-input" />
                </div>
                <div>
                  <label className={ccLabel}>Date</label>
                  <input required type="date" value={meetingForm.date} onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })} className={ccInput} data-testid="meeting-date-input" />
                </div>
                <div>
                  <label className={ccLabel}>Time</label>
                  <input required type="time" value={meetingForm.time} onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })} className={ccInput} data-testid="meeting-time-input" />
                </div>
                <div className="col-span-2">
                  <label className={ccLabel}>Regarding</label>
                  <select value={meetingForm.childId} onChange={(e) => setMeetingForm({ ...meetingForm, childId: e.target.value })} className={ccInput} data-testid="meeting-child-select">
                    <option value="">General</option>
                    {myChildren.map((c) => (
                      <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <button type="submit" className={ccBtnPink} data-testid="meeting-submit-btn">Schedule Meeting</button>
                </div>
              </form>
            )}
            {meetings.length === 0 && !showMeetingForm && <p className="text-xs text-[#9CA3AF] font-medium">No meetings scheduled.</p>}
            {meetings.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-3 bg-[#FFF9F2] border border-[#E6F2FA] rounded-xl mb-2" data-testid={`meeting-row-${m.id}`}>
                <div>
                  <p className="text-xs font-extrabold text-[#374151] flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-[#5CB3DC]" /> {m.title}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF]">{m.date} at {m.time} · with {m.createdByName || m.createdBy}</p>
                </div>
                <a href={m.link} target="_blank" rel="noreferrer" data-testid={`meeting-join-${m.id}`} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold hover:bg-[#D2EBD1] transition-colors shrink-0">
                  <Video className="w-3.5 h-3.5" /> Join
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live events: watch, comment, heart, join (facility members) */}
      <ParentLiveSection currentUser={currentUser} />

      {/* Support & enrollment: complaints + daycare directory (parents only) */}
      {isParent && <ParentSupport currentUser={currentUser} />}

      {/* Messages */}
      <div className={isParent ? "" : "mt-1"}>
        {!isParent && (
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Parent Threads</p>
        )}
        <div className="max-h-56 overflow-y-auto space-y-2 mb-3" data-testid="msg-thread">
          {(isParent ? messages : Object.entries(familyThreads).flatMap(([fam, msgs]) => msgs)).length === 0 && (
            <p className="text-xs text-[#9CA3AF] font-medium">{isParent ? "No messages yet — say hello to the staff!" : "No parent messages yet."}</p>
          )}
          {(isParent ? messages : []).map((m) => (
            <div key={m.id} className={`p-3 rounded-2xl text-xs max-w-[85%] ${m.fromEmail === currentUser?.email ? "ml-auto bg-[#E6F2FA] text-[#374151]" : "bg-[#FFF9F2] border border-[#FCE6E9] text-[#374151]"}`} data-testid={`msg-row-${m.id}`}>
              <p className="font-semibold">{m.text}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">{m.fromName || m.fromEmail} · {fmtTime(m.ts)}</p>
            </div>
          ))}
          {!isParent &&
            Object.entries(familyThreads).map(([fam, msgs]) => (
              <div key={fam} className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`msg-family-${fam}`}>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-1.5">{fam}</p>
                {msgs.slice(-3).map((m) => (
                  <p key={m.id} className="text-xs text-[#374151] font-medium mb-1">
                    <span className="font-extrabold">{m.fromName || m.fromEmail}:</span> {m.text}
                  </p>
                ))}
                <button onClick={() => setReplyTo(fam)} className="text-[10px] font-bold text-[#5CB3DC] hover:underline" data-testid={`msg-reply-${fam}`}>
                  Reply to this family
                </button>
              </div>
            ))}
        </div>
        <form onSubmit={sendMessage} className="flex gap-2">
          <input
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            className={ccInput}
            placeholder={isParent ? "Message the staff…" : replyTo ? `Reply to ${replyTo}…` : "Select a thread to reply…"}
            disabled={!isParent && !replyTo}
            data-testid="msg-input"
          />
          <button type="submit" className="shrink-0 w-11 h-11 rounded-full bg-[#F8C8DC] hover:bg-[#EAAFC5] flex items-center justify-center text-[#374151] transition-colors" data-testid="msg-send-btn">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
